import axios from "axios";

import type { ChatbotStreamEvent, ChatTurn, MultiChatbotResponse } from "../types/multiChatbot";

const AI_BASE = process.env.REACT_APP_AI_CHATBOT_URL?.replace(/\/$/, "") ?? "";

/** Same JWT as dashboard API; AI service expects `Authorization: Bearer`. */
function getAiChatbotRequestHeaders(includeJsonContentType: boolean): Record<string, string> {
  const headers: Record<string, string> = {};
  if (includeJsonContentType) {
    headers["Content-Type"] = "application/json";
  }
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function formatStreamFetchError(res: Response): Promise<string> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    try {
      body = await res.text();
    } catch {
      body = "";
    }
  }
  const detail =
    body && typeof body === "object" && "detail" in body
      ? (body as { detail: unknown }).detail
      : body;
  if (typeof detail === "string") return `${res.status}: ${detail}`;
  return `${res.status}: ${JSON.stringify(detail, null, 2)}`;
}

export interface PostMultiChatbotOptions {
  userQuestion: string;
  chatHistory?: ChatTurn[];
}

/** Multi-agent SQL / analytics chatbot (`POST /chatbot`). */
export function postMultiChatbot(options: PostMultiChatbotOptions): Promise<MultiChatbotResponse> {
  const q = options.userQuestion?.trim() ?? "";
  if (!q) {
    return Promise.reject(new Error("Question is required."));
  }
  if (!AI_BASE) {
    return Promise.reject(new Error("REACT_APP_AI_CHATBOT_URL is not set"));
  }
  const body: { user_question: string; chat_history?: ChatTurn[] } = {
    user_question: q,
  };
  if (options.chatHistory && options.chatHistory.length > 0) {
    body.chat_history = options.chatHistory;
  }
  return axios
    .post<MultiChatbotResponse>(`${AI_BASE}/chatbot`, body, {
      headers: getAiChatbotRequestHeaders(true),
      timeout: 120_000,
    })
    .then((res) => res.data);
}

/** Stream multi-chatbot via SSE (`POST /chatbot/stream`). */
export async function* postMultiChatbotStream(
  options: PostMultiChatbotOptions
): AsyncGenerator<ChatbotStreamEvent> {
  const q = options.userQuestion?.trim() ?? "";
  if (!q) throw new Error("Question is required.");
  if (!AI_BASE) throw new Error("REACT_APP_AI_CHATBOT_URL is not set");

  const body: { user_question: string; chat_history?: ChatTurn[] } = { user_question: q };
  if (options.chatHistory && options.chatHistory.length > 0) {
    body.chat_history = options.chatHistory;
  }

  const res = await fetch(`${AI_BASE}/chatbot/stream`, {
    method: "POST",
    headers: getAiChatbotRequestHeaders(true),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    if (res.status === 404) {
      throw new Error("Streaming endpoint unavailable (404). Ensure the AI service exposes POST /chatbot/stream.");
    }
    throw new Error(await formatStreamFetchError(res));
  }
  if (!res.body) throw new Error("No response body for SSE stream.");

  yield* readSseStream(res.body);
}

/**
 * Stream sales chatbot via SSE (`POST /sales-chatbot/stream`).
 * No auth header required — the sales chatbot endpoint is public.
 * Handles events: status, tool_start, tool_end, thinking, answer_token, done, error.
 */
export async function* postSalesChatbotStream(
  options: PostMultiChatbotOptions
): AsyncGenerator<ChatbotStreamEvent> {
  const q = options.userQuestion?.trim() ?? "";
  if (!q) throw new Error("Question is required.");
  if (!AI_BASE) throw new Error("REACT_APP_AI_CHATBOT_URL is not set");

  const body: { user_question: string; chat_history?: ChatTurn[] } = { user_question: q };
  if (options.chatHistory && options.chatHistory.length > 0) {
    body.chat_history = options.chatHistory;
  }

  const res = await fetch(`${AI_BASE}/sales-chatbot/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    if (res.status === 404) {
      throw new Error("Streaming endpoint unavailable (404). Ensure the AI service exposes POST /sales-chatbot/stream.");
    }
    throw new Error(await formatStreamFetchError(res));
  }
  if (!res.body) throw new Error("No response body for SSE stream.");

  yield* readSseStream(res.body);
}

async function* readSseStream(body: ReadableStream<Uint8Array>): AsyncGenerator<ChatbotStreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      const blocks = buf.split("\n\n");
      buf = blocks.pop() ?? "";

      for (const block of blocks) {
        for (const line of block.split("\n")) {
          const payload = line.startsWith("data:") ? line.slice(5).trim() : null;
          if (!payload) continue;
          try {
            const ev = JSON.parse(payload) as ChatbotStreamEvent;
            yield ev;
            if (ev.type === "done" || ev.type === "error") return;
          } catch {
            /* skip malformed line */
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
